import { BLOCKS, isOperated } from '@lofi/shared';

/**
 * What a right-click means, when the crosshair is on a block.
 *
 * 🔴 **Extracted because the fire shipped UNREACHABLE and nothing could have
 * caught it.** The order of the checks in `main.ts` *is* the feature, it had
 * never been written down as a rule, and it lived inside a mouse handler in the
 * one file the test suite does not touch.
 *
 * ## The order, and why it is this way round
 *
 * | | |
 * |---|---|
 * | 1️⃣ **The block's own use** | A bench, a hearth. **Beats placing** |
 * | 2️⃣ **Placing what you hold** | The ordinary case |
 * | 3️⃣ **Harvesting** | A berry bush, **empty-handed only** |
 *
 * 🎯 **haugur decided rung 1 for the bench**, 2026-08-26: *"if i right click the
 * smíðabekkur with something in my hand it is placed on top, to get the crafting
 * menu my hands need to be empty, right clicking it should alwas open up
 * crafting."*
 *
 * 🔴 **The hearth is the same kind of thing and was missed.** You feed a fire
 * *with something in your hand* — a log — so a hearth that lost to placing could
 * never be fed at all. **It was shipped that way**, and only a question about
 * how to test it found it.
 *
 * ⚠️ **Berries are deliberately NOT on rung 1.** A berry bush is scenery you
 * harvest, not a thing you operate, and promoting it would stop you building
 * against a bush — a change nobody asked for.
 *
 * ## ⚠️ The cost, which is real and already accepted
 *
 * **You cannot place a block by aiming at a bench or a hearth.** Aim at the
 * ground beside it. 🎯 haugur was offered Minecraft's answer — sneak to place —
 * and declined it: `Shift` already means descend-while-flying and
 * run-on-the-ground, and a third meaning on one key is worse than a small fiddle.
 */

/** What the click should do. */
export type Interaction =
  /** Open this block's screen — a crafting station. */
  | 'station'
  /** Tell the server the block was used — a hearth, a berry bush. */
  | 'use'
  /** Put the held block into the world. */
  | 'place'
  /** Nothing to do. The caller explains why. */
  | 'none';

export interface InteractionQuery {
  /** The block under the crosshair, or `undefined` if there is none. */
  readonly blockKey: string | undefined;
  /** Station keys, from the recipes — never a hand-written list. */
  readonly stations: ReadonlySet<string>;
  /** Blocks that are OPERATED rather than built against. */
  readonly operated: ReadonlySet<string>;
  /** The block key the held item would place, or `null` for nothing placeable. */
  readonly holding: string | null;
  /**
   * 🪵 **Every block you take with an empty hand** — the berry bush from
   * `tuning.json`, and every block flagged `pickedByHand`.
   *
   * 🔴 **A SET since 2026-09-02, and it used to be one key.** Driftwood was the
   * second such block and fell straight through the gap: the click did nothing
   * at all, and haugur found it in play within minutes of the first heap
   * landing. **A field that can only hold one thing is a list of one wearing a
   * disguise.**
   */
  readonly harvestable: ReadonlySet<string>;
  /**
   * 🌾 **The fields the item in your hand CUTS** — D131. With a ljár it is
   * `CUTTABLE_FIELDS` (tún, mýri, gras); with anything else it is empty.
   *
   * 🔴 **A set of blocks, not a flag**, so this file never learns what a ljár
   * is — the caller says what the hand can cut, as `holding` says what it
   * can place.
   */
  readonly cuts: ReadonlySet<string>;
}

/**
 * 🔴 **The one place the order lives.**
 *
 * ⚠️ **A station is checked before an operated block** even though a block could
 * in principle be both. Nothing is both today; if something ever is, opening its
 * screen is the more specific answer and the more useful one.
 */
export function interactionFor(q: InteractionQuery): Interaction {
  if (q.blockKey === undefined) return q.holding === null ? 'none' : 'place';

  /* 1️⃣ The block's own use, and it beats placing. */
  if (q.stations.has(q.blockKey)) return 'station';
  if (q.operated.has(q.blockKey)) return 'use';

  /* 2️⃣ Placing what you hold. */
  if (q.holding !== null) return 'place';

  /* 🌾 Cutting a field with the ljár — D131. ⚠️ **A ljár places nothing**, so
     it can never be the turf-in-hand that `place` answers: holding `gras`
     and right-clicking grass still builds, and only the ljár cuts. */
  if (q.cuts.has(q.blockKey)) return 'use';

  /* 3️⃣ Harvesting, empty-handed only. ⚠️ AFTER placing, deliberately: a berry
     bush is scenery you harvest, not a thing you operate, and promoting it
     would stop you building against one. Driftwood joins it on the same rung
     for the same reason — 🎯 haugur: "use EMPTY HAND to pick it up". */
  if (q.harvestable.has(q.blockKey)) return 'use';

  return 'none';
}

/**
 * 🔥 **Blocks you operate rather than build against.**
 *
 * ⚠️ **A hand-written list, and it is the weak point of this file.** There is no
 * field on a block saying *"this does something when you click it"* — the server
 * decides that by branching in `useBlock` — so nothing makes these two agree.
 * 👉 **The day a second operated block exists, this is what gets forgotten**,
 * exactly as the hearth was. **The real fix is a flag in `block-behaviour.json`**
 * that both sides read; that is worth doing when there is a second one, and
 * inventing it for a list of one would be machinery with nothing in it.
 */
export const OPERATED_BLOCKS: ReadonlySet<string> = new Set(
  BLOCKS.filter((b) => isOperated(b.id)).map((b) => b.key),
);
